import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { isDesktopBreakpoint, isTouchDevice, lockScroll, unlockScroll } from '@theme/utilities';

/**
 * @typedef {object} HeaderRefs
 * @property {HTMLDialogElement} drawer - The mobile navigation drawer.
 * @property {HTMLElement} [cartCount] - The element holding the cart item count.
 */

/**
 * Page offset, in pixels, past which the header takes its scrolled state. Small
 * on purpose: the wordmark should start shrinking the moment the page moves.
 */
const SCROLL_THRESHOLD = 10;

/**
 * Header navigation: the desktop submenu popovers, the mobile drawer, and the
 * scrolled state.
 *
 * Submenus open on hover for pointer users and on click for everyone, so the
 * menu stays operable by keyboard and on touch devices where hover never fires.
 *
 * Scrolling only sets `data-scrolled` on the root element. What that means —
 * the wordmark shrinking into the bar, the announcement standing down, the bar
 * taking a background — is left entirely to CSS.
 *
 * @extends {Component<HeaderRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['drawer'];

  /** @type {HTMLButtonElement | null} */
  #openTrigger = null;

  /** @type {number} */
  #frame = 0;

  /** @type {boolean | null} */
  #scrolled = null;

  /**
   * Height of the announcement bar, or 0 when there is none. Measured rather
   * than read back from the setting, so a message that wraps past its minimum
   * height still hands the header the number it is actually standing on.
   *
   * @type {number}
   */
  #announcementHeight = 0;

  /** @type {number | null} */
  #offset = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('pointerleave', this.#handlePointerLeave);

    // `close` rather than only the close button: Escape and the platform's own
    // dismissal both close the dialog without ever reaching a handler here, and
    // would otherwise leave the page scroll-locked.
    this.refs.drawer.addEventListener('close', this.#handleDrawerClose);
    this.refs.drawer.addEventListener('cancel', this.#handleDrawerCancel);
    this.refs.drawer.addEventListener('click', this.#handleDrawerClick);

    window.addEventListener('scroll', this.#requestScrollUpdate, { passive: true });
    window.addEventListener('resize', this.#measureAnnouncement, { passive: true });

    // The bar is a sibling section: in the theme editor it can come, go and
    // change height without this header ever reconnecting.
    document.addEventListener('shopify:section:load', this.#measureAnnouncement);
    document.addEventListener('shopify:section:unload', this.#measureAnnouncement);

    this.#measureAnnouncement();
    this.#updateScrollState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);

    this.refs.drawer.removeEventListener('close', this.#handleDrawerClose);
    this.refs.drawer.removeEventListener('cancel', this.#handleDrawerCancel);
    this.refs.drawer.removeEventListener('click', this.#handleDrawerClick);

    window.removeEventListener('scroll', this.#requestScrollUpdate);
    window.removeEventListener('resize', this.#measureAnnouncement);
    document.removeEventListener('shopify:section:load', this.#measureAnnouncement);
    document.removeEventListener('shopify:section:unload', this.#measureAnnouncement);

    cancelAnimationFrame(this.#frame);
    document.documentElement.removeAttribute('data-scrolled');
    this.style.removeProperty('--header-offset');
  }

  /**
   * Keeps the cart count honest after something adds to the cart without a page
   * load.
   *
   * The header listens for the theme's `cart:update` event rather than being
   * called by whatever did the adding, so the product form, a quick-add button
   * and a future cart drawer all keep it current without knowing this exists.
   *
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    const count = /** @type {{ detail?: { itemCount?: number } }} */ (event).detail?.itemCount;

    if (typeof count !== 'number' || !this.refs.cartCount) return;

    this.refs.cartCount.textContent = String(count);
  };

  #requestScrollUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#updateScrollState);
  };

  #updateScrollState = () => {
    const scrolled = window.scrollY >= SCROLL_THRESHOLD;

    if (scrolled !== this.#scrolled) {
      this.#scrolled = scrolled;
      document.documentElement.toggleAttribute('data-scrolled', scrolled);
    }

    this.#updateOffset();
  };

  /**
   * How far the top of the page is out of reach, which is what the fixed parts
   * of the header position themselves against.
   *
   * An announcement bar sits above the header in the flow and scrolls away with
   * the page, so the header has to ride up with it and then pin to the top —
   * exactly what `position: sticky` would do if the header could afford to be
   * sticky. It cannot: it has to lie over a hero while contributing no height of
   * its own, so the travel is worked out here instead.
   *
   * Nothing is written when there is no bar, so those pages carry no inline
   * styles from this at all and the CSS resting value stands.
   */
  #updateOffset = () => {
    if (this.#announcementHeight === 0) return;

    const offset = Math.max(0, this.#announcementHeight - window.scrollY);

    if (offset === this.#offset) return;
    this.#offset = offset;

    this.style.setProperty('--header-offset', `${offset}px`);
  };

  #measureAnnouncement = () => {
    const bar = document.querySelector('.announcement');

    this.#announcementHeight = bar instanceof HTMLElement ? bar.offsetHeight : 0;
    this.#offset = null;

    if (this.#announcementHeight === 0) {
      this.style.removeProperty('--header-offset');
      return;
    }

    this.#updateOffset();
  };

  /**
   * Toggles the submenu belonging to the clicked trigger.
   * @param {PointerEvent} event
   */
  toggleSubmenu(event) {
    const trigger = event.target;
    if (!(trigger instanceof HTMLButtonElement)) return;

    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    this.#closeSubmenus();
    if (!isOpen) this.#setSubmenuState(trigger, true);
  }

  /**
   * Opens a submenu when a pointer enters its trigger.
   *
   * Skipped on touch devices and below the desktop breakpoint, where the click
   * handler owns the interaction and a hover-open would fight the tap.
   *
   * @param {PointerEvent} event
   */
  openSubmenu(event) {
    if (isTouchDevice() || !isDesktopBreakpoint()) return;

    const trigger = event.target;
    if (!(trigger instanceof HTMLButtonElement)) return;

    this.#closeSubmenus();
    this.#setSubmenuState(trigger, true);
  }

  /** Opens the mobile navigation drawer. */
  toggleDrawer() {
    const { drawer } = this.refs;

    if (drawer.open) {
      this.closeDrawer();
      return;
    }

    this.#showDrawerPanel(null);
    drawer.showModal();
    lockScroll(this);
    this.dataset.menuOpen = 'true';
  }

  /** Closes the mobile navigation drawer. */
  closeDrawer() {
    this.refs.drawer.close();
  }

  /**
   * Slides in the panel belonging to a menu item that has children.
   *
   * @param {PointerEvent} event
   */
  openDrawerPanel(event) {
    const trigger = event.target;
    if (!(trigger instanceof HTMLElement)) return;

    const panel = this.#drawerPanel(trigger.dataset.panel);
    if (!panel) return;

    this.#showDrawerPanel(panel);

    // Focus lands on the way out rather than on the first link, so the panel is
    // announced by what it is and the way back is the next thing to hand.
    const back = panel.querySelector('.header__drawer-back');
    if (back instanceof HTMLElement) back.focus();
  }

  /** Returns from a submenu panel to the root menu. */
  closeDrawerPanel() {
    const open = this.refs.drawer.querySelector('.header__drawer-panel--child[data-active]');

    this.#showDrawerPanel(null);

    if (!(open instanceof HTMLElement)) return;

    const trigger = this.refs.drawer.querySelector(`[data-panel="${CSS.escape(open.id)}"]`);
    if (trigger instanceof HTMLElement) trigger.focus();
  }

  /**
   * @param {string} [id]
   * @returns {HTMLElement | null}
   */
  #drawerPanel(id) {
    if (!id) return null;

    const panel = this.refs.drawer.querySelector(`#${CSS.escape(id)}`);

    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * Brings one panel into view and takes every other one out of reach.
   *
   * `inert` is what does the work: the panels out of view are still painted,
   * sitting outside the drawer's clip, so without it they stay tabbable and
   * readable to a screen reader.
   *
   * @param {HTMLElement | null} panel - The submenu panel to show, or null for the root menu.
   */
  #showDrawerPanel(panel) {
    const { drawer } = this.refs;

    drawer.toggleAttribute('data-submenu', Boolean(panel));

    for (const element of drawer.querySelectorAll('.header__drawer-panel')) {
      if (!(element instanceof HTMLElement)) continue;

      const active = panel ? element === panel : element.classList.contains('header__drawer-panel--root');

      element.toggleAttribute('data-active', active);
      element.inert = !active;
    }

    for (const trigger of drawer.querySelectorAll('[data-panel]')) {
      if (!(trigger instanceof HTMLElement)) continue;

      trigger.setAttribute('aria-expanded', String(Boolean(panel) && trigger.dataset.panel === panel?.id));
    }
  }

  /**
   * The drawer is narrower than the screen, so a press that lands on the
   * backdrop reports the dialog itself as its target — nothing inside it can.
   *
   * @param {MouseEvent} event
   */
  #handleDrawerClick = (event) => {
    if (event.target === this.refs.drawer) this.closeDrawer();
  };

  /**
   * Escape steps back out of a submenu before it closes the drawer, so the two
   * levels are dismissed in the order they were opened.
   *
   * @param {Event} event
   */
  #handleDrawerCancel = (event) => {
    if (!this.refs.drawer.hasAttribute('data-submenu')) return;

    event.preventDefault();
    this.closeDrawerPanel();
  };

  #handleDrawerClose = () => {
    unlockScroll(this);
    this.dataset.menuOpen = 'false';
    this.#showDrawerPanel(null);
  };

  /**
   * @param {HTMLButtonElement} trigger
   * @param {boolean} open
   */
  #setSubmenuState(trigger, open) {
    const id = trigger.getAttribute('aria-controls');
    const submenu = id && this.querySelector(`#${CSS.escape(id)}`);

    if (!(submenu instanceof HTMLElement)) return;

    trigger.setAttribute('aria-expanded', String(open));
    submenu.hidden = !open;

    if (open) this.#openTrigger = trigger;
  }

  #closeSubmenus() {
    for (const trigger of this.querySelectorAll('[aria-expanded="true"][aria-controls]')) {
      if (trigger instanceof HTMLButtonElement) this.#setSubmenuState(trigger, false);
    }

    this.#openTrigger = null;
  }

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#closeSubmenus();
  };

  #handlePointerLeave = () => {
    if (isTouchDevice() || !isDesktopBreakpoint()) return;

    this.#closeSubmenus();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const trigger = this.#openTrigger;

    this.#closeSubmenus();
    trigger?.focus();
  };
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
